import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchWithAuth } from '@/data/api/client'
import type {
  NotificationConfiguration,
  CreateNotificationRequest,
  UpdateNotificationRequest,
  NotificationHistoryResponse,
  NotificationsListResponse,
} from '@/data/types/alerts.types'

async function listNotifications(params?: { limit?: number; offset?: number }): Promise<NotificationsListResponse> {
  const searchParams = new URLSearchParams()
  if (params?.limit !== undefined) searchParams.set('limit', String(params.limit))
  if (params?.offset !== undefined) searchParams.set('offset', String(params.offset))
  const query = searchParams.toString()
  return fetchWithAuth(`/api/v1/notifications${query ? `?${query}` : ''}`)
}

async function getNotificationById(id: string): Promise<NotificationConfiguration> {
  return fetchWithAuth(`/api/v1/notifications/${id}`)
}

async function createNotification(data: CreateNotificationRequest): Promise<NotificationConfiguration> {
  return fetchWithAuth('/api/v1/notifications', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

async function updateNotification(id: string, data: UpdateNotificationRequest): Promise<NotificationConfiguration> {
  return fetchWithAuth(`/api/v1/notifications/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

async function deleteNotification(id: string): Promise<void> {
  return fetchWithAuth(`/api/v1/notifications/${id}`, {
    method: 'DELETE',
  })
}

async function getNotificationHistory(id: string, limit: number = 50, offset: number = 0): Promise<NotificationHistoryResponse> {
  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
  })
  return fetchWithAuth(`/api/v1/notifications/${id}/history?${params}`)
}

export function useNotifications(params?: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['notifications', params],
    queryFn: () => listNotifications(params),
  })
}

export function useNotification(id: string | null) {
  return useQuery({
    queryKey: ['notifications', id],
    queryFn: () => getNotificationById(id!),
    enabled: !!id,
  })
}

export function useCreateNotification() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

export function useUpdateNotification() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateNotificationRequest }) =>
      updateNotification(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

export function useDeleteNotification() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

export function useNotificationHistory(id: string | null, limit: number = 50, offset: number = 0) {
  return useQuery({
    queryKey: ['notifications', id, 'history', limit, offset],
    queryFn: () => getNotificationHistory(id!, limit, offset),
    enabled: !!id,
  })
}

